import React from 'react';
import { Phone, MessageCircle, FileText, Users, Code, CheckCircle } from 'lucide-react';

const ServicesContact: React.FC = () => {
  const phoneNumber = '(424) 244-6608';
  const phoneNumberClean = '4242446608';

  return (
    <div className="relative min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="pt-28 pb-16 relative bg-white">
        <div className="max-w-4xl mx-auto px-10">
          {/* Glass Container */}
          <div className="relative bg-gradient-to-br from-blue-50/60 via-white/90 to-cyan-50/60 backdrop-blur-xl rounded-3xl p-12 shadow-[0_8px_30px_rgba(59,130,246,0.15),0_0_60px_rgba(59,130,246,0.1)] border border-blue-100/40">
            <div className="text-center overflow-visible">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-8 leading-[1.5] tracking-tight pb-4 overflow-visible">
                Let's Build Something
                <span className="block mt-4 bg-gradient-to-r from-slate-700 to-slate-500 bg-clip-text text-transparent pb-2 overflow-visible">
                  Amazing Together
                </span>
              </h1>
              
              <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-12 leading-relaxed">
                Ready to bring your vision to life? Our team is here to help you every step of the way.
              </p>

              {/* Phone Number Display - Featured */}
              <div className="w-full max-w-2xl mx-auto">
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-10 shadow-xl border border-slate-700/30 relative overflow-hidden">
                  {/* Phone Imprint */}
                  <div className="absolute right-8 top-1/2 -translate-y-1/2 opacity-[0.04]">
                    <Phone className="w-48 h-48 text-white" strokeWidth={1} />
                  </div>
                  
                  <div className="relative text-center">
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Call or Text</p>
                    <a 
                      href={`tel:${phoneNumberClean}`}
                      className="text-4xl sm:text-5xl font-bold text-white hover:text-slate-200 transition-colors block mb-3"
                    >
                      {phoneNumber}
                    </a>
                    <p className="text-sm text-slate-400">Available Monday - Friday, 9am - 6pm PST</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              How We Work Together
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A simple, collaborative process designed to deliver exactly what you need.
            </p>
          </div>

          {/* Process Steps - Timeline */}
          <div className="relative mb-16">
            {/* Desktop Timeline */}
            <div className="hidden lg:block">
              <div className="relative">
                {/* Dashed Line connecting centers of circles */}
                <div className="absolute top-8 left-0 right-0 flex items-center justify-between px-[12.5%]">
                  <div className="flex-1 border-t-2 border-dashed border-slate-300"></div>
                </div>
                
                <div className="grid grid-cols-4 gap-8">
                  {/* Step 1 */}
                  <div className="relative group flex flex-col">
                    {/* Step Number Circle */}
                    <div className="relative z-10 mb-8 flex justify-center">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-900 to-slate-700 border-4 border-white shadow-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <span className="text-2xl font-bold text-white">1</span>
                      </div>
                    </div>
                    
                    <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden flex-1 flex flex-col">
                      {/* Imprint Icon */}
                      <div className="absolute -right-6 -bottom-6 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity">
                        <MessageCircle className="w-36 h-36 text-slate-900" strokeWidth={1} />
                      </div>
                      
                      <div className="relative flex-1">
                        <h3 className="text-xl font-bold text-slate-900 mb-3">Discovery Call</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          We'll discuss your needs, goals, and vision. Share your ideas and challenges with us.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="relative group flex flex-col">
                    {/* Step Number Circle */}
                    <div className="relative z-10 mb-8 flex justify-center">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-900 to-slate-700 border-4 border-white shadow-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <span className="text-2xl font-bold text-white">2</span>
                      </div>
                    </div>
                    
                    <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden flex-1 flex flex-col">
                      {/* Imprint Icon */}
                      <div className="absolute -right-6 -bottom-6 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity">
                        <FileText className="w-36 h-36 text-slate-900" strokeWidth={1} />
                      </div>
                      
                      <div className="relative flex-1">
                        <h3 className="text-xl font-bold text-slate-900 mb-3">Wireframes & Planning</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          Our team creates detailed wireframes and project plans to visualize your solution.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="relative group flex flex-col">
                    {/* Step Number Circle */}
                    <div className="relative z-10 mb-8 flex justify-center">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-900 to-slate-700 border-4 border-white shadow-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <span className="text-2xl font-bold text-white">3</span>
                      </div>
                    </div>
                    
                    <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden flex-1 flex flex-col">
                      {/* Imprint Icon */}
                      <div className="absolute -right-6 -bottom-6 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity">
                        <Users className="w-36 h-36 text-slate-900" strokeWidth={1} />
                      </div>
                      
                      <div className="relative flex-1">
                        <h3 className="text-xl font-bold text-slate-900 mb-3">Team Collaboration</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          Our developers and designers work together to bring your project to life.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="relative group flex flex-col">
                    {/* Step Number Circle */}
                    <div className="relative z-10 mb-8 flex justify-center">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-900 to-slate-700 border-4 border-white shadow-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <span className="text-2xl font-bold text-white">4</span>
                      </div>
                    </div>
                    
                    <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden flex-1 flex flex-col">
                      {/* Imprint Icon */}
                      <div className="absolute -right-6 -bottom-6 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity">
                        <Code className="w-36 h-36 text-slate-900" strokeWidth={1} />
                      </div>
                      
                      <div className="relative flex-1">
                        <h3 className="text-xl font-bold text-slate-900 mb-3">Build & Launch</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          We develop, test, and deploy your solution with ongoing support every step of the way.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile/Tablet Vertical Timeline */}
            <div className="lg:hidden space-y-8">
              {/* Step 1 */}
              <div className="relative flex gap-6 group">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-slate-900 border-4 border-white shadow-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <span className="text-lg font-bold text-white">1</span>
                  </div>
                  <div className="w-0.5 flex-1 border-l-2 border-dashed border-slate-300 mt-2"></div>
                </div>
                
                <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden flex-1">
                  <div className="absolute -right-4 -bottom-4 opacity-[0.03]">
                    <MessageCircle className="w-24 h-24 text-slate-900" strokeWidth={1} />
                  </div>
                  <div className="relative">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Discovery Call</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      We'll discuss your needs, goals, and vision. Share your ideas and challenges with us.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative flex gap-6 group">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-slate-900 border-4 border-white shadow-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <span className="text-lg font-bold text-white">2</span>
                  </div>
                  <div className="w-0.5 flex-1 border-l-2 border-dashed border-slate-300 mt-2"></div>
                </div>
                
                <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden flex-1">
                  <div className="absolute -right-4 -bottom-4 opacity-[0.03]">
                    <FileText className="w-24 h-24 text-slate-900" strokeWidth={1} />
                  </div>
                  <div className="relative">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Wireframes & Planning</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Our team creates detailed wireframes and project plans to visualize your solution.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative flex gap-6 group">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-slate-900 border-4 border-white shadow-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <span className="text-lg font-bold text-white">3</span>
                  </div>
                  <div className="w-0.5 flex-1 border-l-2 border-dashed border-slate-300 mt-2"></div>
                </div>
                
                <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden flex-1">
                  <div className="absolute -right-4 -bottom-4 opacity-[0.03]">
                    <Users className="w-24 h-24 text-slate-900" strokeWidth={1} />
                  </div>
                  <div className="relative">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Team Collaboration</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Our developers and designers work together to bring your project to life.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="relative flex gap-6 group">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-slate-900 border-4 border-white shadow-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <span className="text-lg font-bold text-white">4</span>
                  </div>
                </div>
                
                <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden flex-1">
                  <div className="absolute -right-4 -bottom-4 opacity-[0.03]">
                    <Code className="w-24 h-24 text-slate-900" strokeWidth={1} />
                  </div>
                  <div className="relative">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Build & Launch</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      We develop, test, and deploy your solution with ongoing support every step of the way.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              What to Expect
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              When you reach out, here's what happens next
            </p>
          </div>

          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Large Box - Quick Response */}
            <div className="md:col-span-2 md:row-span-2 bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-200 shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.2)] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_0_0_1px_rgba(148,163,184,0.3),0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 group relative overflow-hidden">
              <div className="absolute -right-8 -top-8 opacity-[0.04] group-hover:opacity-[0.06] transition-opacity">
                <CheckCircle className="w-64 h-64 text-slate-900" strokeWidth={1} />
              </div>
              <div className="relative">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-slate-900 text-white font-bold text-xl mb-4 shadow-md">
                  1
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Quick Response</h3>
                <p className="text-slate-600 leading-relaxed text-lg">
                  We'll get back to you within 24 hours to schedule your discovery call. Our team prioritizes every inquiry and ensures you get the attention you deserve.
                </p>
              </div>
            </div>

            {/* No Pressure */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-200 shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.2)] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_0_0_1px_rgba(148,163,184,0.3),0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 group relative overflow-hidden">
              <div className="absolute -bottom-6 -right-6 opacity-[0.04] group-hover:opacity-[0.06] transition-opacity">
                <CheckCircle className="w-32 h-32 text-slate-900" strokeWidth={1} />
              </div>
              <div className="relative">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-slate-900 text-white font-bold text-lg mb-3 shadow-md">
                  2
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">No Pressure</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Free consultation with no obligation. We're here to help, not push.
                </p>
              </div>
            </div>

            {/* Clear Timeline */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-200 shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.2)] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_0_0_1px_rgba(148,163,184,0.3),0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 group relative overflow-hidden">
              <div className="absolute -bottom-6 -right-6 opacity-[0.04] group-hover:opacity-[0.06] transition-opacity">
                <CheckCircle className="w-32 h-32 text-slate-900" strokeWidth={1} />
              </div>
              <div className="relative">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-slate-900 text-white font-bold text-lg mb-3 shadow-md">
                  3
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Clear Timeline</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Detailed timeline and milestones so you know what to expect.
                </p>
              </div>
            </div>

            {/* Transparent Pricing */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-200 shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.2)] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_0_0_1px_rgba(148,163,184,0.3),0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 group relative overflow-hidden">
              <div className="absolute -bottom-6 -right-6 opacity-[0.04] group-hover:opacity-[0.06] transition-opacity">
                <CheckCircle className="w-32 h-32 text-slate-900" strokeWidth={1} />
              </div>
              <div className="relative">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-slate-900 text-white font-bold text-lg mb-3 shadow-md">
                  4
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Transparent Pricing</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Clear pricing with no hidden fees. Everything documented.
                </p>
              </div>
            </div>

            {/* Expert Guidance */}
            <div className="md:col-span-2 bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-200 shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.2)] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_0_0_1px_rgba(148,163,184,0.3),0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 group relative overflow-hidden">
              <div className="absolute -right-8 -bottom-8 opacity-[0.04] group-hover:opacity-[0.06] transition-opacity">
                <CheckCircle className="w-40 h-40 text-slate-900" strokeWidth={1} />
              </div>
              <div className="relative">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-slate-900 text-white font-bold text-lg mb-3 shadow-md">
                  5
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Expert Guidance</h3>
                <p className="text-slate-600 leading-relaxed">
                  Our experienced team will recommend the best technologies and approaches tailored to your specific needs and business goals.
                </p>
              </div>
            </div>

            {/* Ongoing Support */}
            <div className="md:col-span-3 bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-200 shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.2)] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_0_0_1px_rgba(148,163,184,0.3),0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 group relative overflow-hidden">
              <div className="absolute -right-12 -bottom-12 opacity-[0.04] group-hover:opacity-[0.06] transition-opacity">
                <CheckCircle className="w-56 h-56 text-slate-900" strokeWidth={1} />
              </div>
              <div className="relative max-w-3xl">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-slate-900 text-white font-bold text-xl mb-4 shadow-md">
                  6
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Ongoing Support</h3>
                <p className="text-slate-600 leading-relaxed text-lg">
                  We don't disappear after launch. We're here for maintenance, updates, and continuous improvements. Your success is our success, and we're committed for the long term.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 opacity-5">
          <Phone className="w-64 h-64 text-white" strokeWidth={0.5} />
        </div>
        <div className="absolute bottom-10 left-10 opacity-5">
          <MessageCircle className="w-48 h-48 text-white" strokeWidth={0.5} />
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-block mb-4">
            <span className="px-4 py-2 bg-white/10 text-white text-sm font-semibold rounded-full backdrop-blur-sm">
              Let's Talk
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            Give us a call or send a text. We're excited to hear about your project!
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={`tel:${phoneNumberClean}`}
              className="group inline-flex items-center gap-3 px-10 py-5 bg-white text-slate-900 font-bold rounded-full hover:bg-slate-100 hover:scale-105 transition-all duration-300 shadow-2xl active:scale-95 text-lg"
            >
              <Phone className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              Call {phoneNumber}
            </a>
            
            <div className="group inline-flex items-center gap-3 px-10 py-5 bg-white/10 text-white font-bold rounded-full hover:bg-white/20 hover:scale-105 transition-all duration-300 border-2 border-white/20 backdrop-blur-sm text-lg cursor-default">
              <MessageCircle className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              Text {phoneNumber}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesContact;
